import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  normalizeReputationSummary,
  type ReputationSummary,
  type ReputationTarget,
} from './reputationApi';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';

interface PostSessionReputationPanelProps {
  roomCode: string | undefined;
  isSignedIn: boolean | undefined;
  getToken: () => Promise<string | null>;
}

type SubmitState = 'idle' | 'submitting' | 'sent';

function getRoleLabel(role: string | null) {
  if (!role) return null;
  if (role === 'host' || role === 'organizer') return 'Organizer';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function isReviewableSummaryTarget(target: ReputationTarget) {
  return target.role === 'organizer' || target.role === 'host' || target.role === 'model';
}

function getReviewTargets(summary: ReputationSummary) {
  if (!summary.canReview) return [];

  return summary.reviewTargets.filter((target) => (
    isReviewableSummaryTarget(target)
  ));
}

function getInitialScores(targets: ReputationTarget[]) {
  return targets.reduce<Record<string, number>>((scores, target) => {
    if (target.existingReview) {
      scores[target.profileId] = target.existingReview.score;
    }
    return scores;
  }, {});
}

async function readErrorMessage(response: Response, fallback: string) {
  const data = await response.json().catch(() => null);
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    return data.error;
  }
  return fallback;
}

export function PostSessionReputationPanel({ roomCode, isSignedIn, getToken }: PostSessionReputationPanelProps) {
  const [summary, setSummary] = useState<ReputationSummary | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!roomCode || !isSignedIn) {
      setSummary(null);
      return;
    }

    try {
      const token = await getToken();

      const response = await fetch(`/api/reputation/rooms/${roomCode}/summary`, {
        headers: authHeaders(token),
      });
      if (response.status === 404 || response.status === 501) {
        setUnavailable(true);
        setSummary(null);
        return;
      }
      if (!response.ok) {
        setSummary(null);
        return;
      }

      const data = await response.json().catch(() => null);
      const normalized = normalizeReputationSummary(data);
      setSummary(normalized);
      setUnavailable(false);
    } catch (error) {
      console.info('[PostSessionReputationPanel] Reputation API unavailable', error);
      setSummary(null);
    }
  }, [roomCode, isSignedIn, getToken]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  if (!roomCode || !isSignedIn || unavailable || !summary || getReviewTargets(summary).length === 0) {
    return null;
  }

  return (
    <section className="bg-panel border border-line rounded-xl p-5 mb-8" data-testid="post-session-reputation-panel">
      <ReviewForm roomCode={roomCode} summary={summary} getToken={getToken} onSubmitted={loadSummary} />
    </section>
  );
}

function ReviewForm({
  roomCode,
  summary,
  getToken,
  onSubmitted,
}: {
  roomCode: string;
  summary: ReputationSummary;
  getToken: () => Promise<string | null>;
  onSubmitted: () => Promise<void>;
}) {
  const targets = useMemo(() => getReviewTargets(summary), [summary]);
  const [scoresByTarget, setScoresByTarget] = useState<Record<string, number>>({});
  const [submitStateByTarget, setSubmitStateByTarget] = useState<Record<string, SubmitState>>({});
  const [errorByTarget, setErrorByTarget] = useState<Record<string, string | null>>({});

  useEffect(() => {
    setScoresByTarget(getInitialScores(targets));
    setSubmitStateByTarget({});
    setErrorByTarget({});
  }, [targets]);

  if (targets.length === 0) return null;

  const saveReview = async (target: ReputationTarget, selectedScore?: number) => {
    if (submitStateByTarget[target.profileId] === 'submitting') return;

    const score = selectedScore ?? scoresByTarget[target.profileId] ?? 0;
    if (score < 1 || score > 5) {
      setErrorByTarget((current) => ({ ...current, [target.profileId]: 'Choose a rating before saving' }));
      return;
    }

    setSubmitStateByTarget((current) => ({ ...current, [target.profileId]: 'submitting' }));
    setErrorByTarget((current) => ({ ...current, [target.profileId]: null }));
    try {
      const token = await getToken();
      const response = await fetch(`/api/reputation/rooms/${roomCode}/reviews`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          subjectProfileId: target.profileId,
          score,
          note: null,
        }),
      });
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to save a review');
        if (authMessage) throw new Error(authMessage);
        throw new Error(await readErrorMessage(response, 'Could not save review'));
      }

      setSubmitStateByTarget((current) => ({ ...current, [target.profileId]: 'sent' }));
      await onSubmitted();
    } catch (err) {
      setErrorByTarget((current) => ({
        ...current,
        [target.profileId]: err instanceof Error ? err.message : 'Could not save review',
      }));
      setSubmitStateByTarget((current) => ({ ...current, [target.profileId]: 'idle' }));
    }
  };

  return (
    <>
      <h2 className="font-display text-xl font-normal text-text">How did it go?</h2>
      <div className="mt-3 divide-y divide-line-soft">
        {targets.map((target) => {
          const selectedScore = scoresByTarget[target.profileId] ?? 0;
          const savedScore = target.existingReview?.score ?? null;
          const submitState = submitStateByTarget[target.profileId] ?? 'idle';
          const error = errorByTarget[target.profileId];
          const isLocallySaved = submitState === 'sent' && selectedScore > 0;
          const hasSavedScore = selectedScore > 0 && (selectedScore === savedScore || isLocallySaved);

          return (
            <div key={target.profileId} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                {target.photoUrl ? (
                  <img
                    src={target.photoUrl}
                    alt=""
                    className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-line bg-bg-secondary text-sm text-text-dim"
                    aria-hidden="true"
                  >
                    {target.displayName.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm text-text">{target.displayName}</div>
                  {getRoleLabel(target.role) && (
                    <div className="text-xs text-text-dim">{getRoleLabel(target.role)}</div>
                  )}
                  {error && (
                    <div className="mt-1 text-xs text-warn">
                      {error}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <div className="flex items-center gap-1" role="group" aria-label={`Review ${target.displayName}`}>
                  {[1, 2, 3, 4, 5].map((value) => {
                    const active = selectedScore >= value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setScoresByTarget((current) => ({ ...current, [target.profileId]: value }));
                          setSubmitStateByTarget((current) => ({ ...current, [target.profileId]: 'idle' }));
                          setErrorByTarget((current) => ({ ...current, [target.profileId]: null }));
                          if (value !== savedScore || !hasSavedScore) {
                            void saveReview(target, value);
                          }
                        }}
                        disabled={submitState === 'submitting'}
                        className={`h-10 w-10 rounded-lg border text-lg leading-none transition-colors disabled:cursor-wait disabled:opacity-70 ${
                          active
                            ? 'border-accent bg-accent text-white'
                            : 'border-line bg-panel text-text-dim hover:text-text'
                        }`}
                        aria-label={`${value} star${value === 1 ? '' : 's'} for ${target.displayName}`}
                        aria-pressed={active}
                      >
                        ★
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function ModelSafetyCheckGate({
  roomCode,
  isSignedIn,
  getToken,
  onCompleted,
}: {
  roomCode: string | undefined;
  isSignedIn: boolean | undefined;
  getToken: () => Promise<string | null>;
  onCompleted: () => void;
}) {
  const [summary, setSummary] = useState<ReputationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedChoice, setSelectedChoice] = useState<'safe' | 'concern' | null>(null);
  const [concernSelected, setConcernSelected] = useState(false);
  const [note, setNote] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [error, setError] = useState<string | null>(null);

  const submitSafetyCheck = useCallback(async (feltSafe: boolean, concernNote: string | null) => {
    if (!roomCode || submitState === 'submitting') return;

    setSubmitState('submitting');
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/reputation/rooms/${roomCode}/model-safety-survey`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          feltSafe,
          note: concernNote,
        }),
      });
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to submit the safety check');
        if (authMessage) throw new Error(authMessage);
        throw new Error(await readErrorMessage(response, 'Could not submit safety check'));
      }

      setSubmitState('sent');
      onCompleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit safety check');
      setSubmitState('idle');
      setSelectedChoice(null);
    }
  }, [getToken, onCompleted, roomCode, submitState]);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      if (!roomCode || !isSignedIn) {
        onCompleted();
        return;
      }

      setLoading(true);
      try {
        const token = await getToken();

        const response = await fetch(`/api/reputation/rooms/${roomCode}/summary`, {
          headers: authHeaders(token),
        });
        if (!response.ok) {
          onCompleted();
          return;
        }

        const data = await response.json().catch(() => null);
        const normalized = normalizeReputationSummary(data);
        if (cancelled) return;

        const safetyCheckPending = Boolean(
          normalized?.canSubmitSafetySurvey && !normalized.safetySurvey?.submitted
        );
        if (!safetyCheckPending) {
          onCompleted();
          return;
        }

        setSummary(normalized);
      } catch {
        if (!cancelled) onCompleted();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadSummary();

    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, onCompleted, roomCode]);

  if (loading || !summary) {
    return (
      <div className="text-sm text-text-dim">Loading safety check...</div>
    );
  }

  const canSubmitConcern = concernSelected && note.trim().length > 0 && submitState !== 'submitting';

  return (
    <section className="w-full max-w-[560px] rounded-xl border border-line bg-panel p-6 shadow-sm">
      <h1 className="font-display text-2xl font-normal text-text">Safety check</h1>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => {
            setSelectedChoice('safe');
            void submitSafetyCheck(true, null);
          }}
          disabled={submitState === 'submitting'}
          className={`btn flex-1 ${selectedChoice === 'safe' ? 'btn-accent' : 'btn-secondary'}`}
        >
          I felt safe
        </button>
        <button
          type="button"
          onClick={() => {
            setSelectedChoice('concern');
            setConcernSelected(true);
          }}
          disabled={submitState === 'submitting'}
          className={`btn flex-1 ${selectedChoice === 'concern' ? 'btn-accent' : 'btn-secondary'}`}
          aria-pressed={concernSelected}
        >
          I have a concern
        </button>
      </div>

      {concernSelected && (
        <form
          className="mt-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmitConcern) {
              void submitSafetyCheck(false, note.trim());
            }
          }}
        >
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={6}
            className="input h-auto min-h-[180px] w-full resize-none py-3"
            placeholder="What should admins know?"
            required
          />
          <p className="type-caption mt-2 text-text-dim">
            Only visible to lifedrawing.live admins
          </p>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="type-caption text-warn">{error}</div>
            <button type="submit" className="btn btn-accent btn-sm" disabled={!canSubmitConcern}>
              {submitState === 'submitting' ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      )}

      {!concernSelected && error && (
        <div className="mt-3 text-xs text-warn">{error}</div>
      )}
    </section>
  );
}
